import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';

import {
  useCreateTimeEditRequestMutation,
  useGetProjectsQuery,
  useGetTasksListQuery
} from '../../../redux/apis/timeTrackingApi';
import { wallTimeOnAnchorDayToUtcMs } from '../../../utils/timedoctorAttendanceHelpers';
import { notification } from '../../../utils/notification';
import { TOAST_TYPE } from '../../../utils/constant/errorMessage';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const normalizeId = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value._id || value.id || null;
  return value;
};

const extractListRows = (res) => {
  const candidates = [
    res?.data?.result,
    res?.data?.docs,
    res?.data?.tasks,
    res?.data?.data?.docs,
    res?.data?.data?.result,
    res?.data?.data?.tasks,
    res?.data?.data?.data,
    res?.data?.data,
    res?.result,
    res?.docs,
    res?.data,
    res
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

const selectStyles = (error) => ({
  menuPortal: (base) => ({ ...base, zIndex: 100050 }),
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: error ? '#dc3545' : '#e5e7eb',
    boxShadow: 'none'
  })
});

const formatHhmmTo12h = (hhmm) => {
  if (!hhmm || !String(hhmm).includes(':')) return hhmm || '';
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return String(hhmm);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

const toMinutesOfDay = (hhmm) => {
  if (!hhmm || !String(hhmm).includes(':')) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const fromMinutesOfDay = (totalMins) => {
  const h = Math.floor(totalMins / 60) % 24;
  const m = totalMins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatHourTo12hLabel = (hour24) => {
  if (!Number.isFinite(hour24)) return '';
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12} ${period}`;
};

/** 12-hour AM/PM time picker constrained to [minTime, maxTime] window. */
const TimePicker12h = ({
  value,
  minTime = '00:00',
  maxTime = '23:59',
  onChange,
  placeholder = 'Select time',
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const minMins = toMinutesOfDay(minTime) ?? 0;
  const maxMins = toMinutesOfDay(maxTime) ?? 1439;

  const hours = useMemo(() => {
    const list = [];
    for (let h = Math.floor(minMins / 60); h <= Math.floor(maxMins / 60); h += 1) {
      list.push(h);
    }
    return list;
  }, [minMins, maxMins]);

  const selectedHour = value ? Number(String(value).split(':')[0]) : hours[0];
  const selectedMinute = value ? Number(String(value).split(':')[1]) : null;

  const minutes = useMemo(() => {
    if (!Number.isFinite(selectedHour)) return [];
    const list = [];
    for (let m = 0; m < 60; m += 1) {
      const total = selectedHour * 60 + m;
      if (total >= minMins && total <= maxMins) list.push(m);
    }
    return list;
  }, [minMins, maxMins, selectedHour]);

  const updateMenuPosition = () => {
    const node = rootRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const menuHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom;
    const shouldOpenUp = spaceBelow < menuHeight && rect.top > menuHeight;
    setMenuPos({
      top: shouldOpenUp ? rect.top - 6 : rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 180),
      openUp: shouldOpenUp
    });
  };

  useEffect(() => {
    if (!open || disabled) return undefined;
    updateMenuPosition();
    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onReposition = () => updateMenuPosition();
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, disabled]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const pickHour = (hour) => {
    const hourMinutes = [];
    for (let m = 0; m < 60; m += 1) {
      const total = hour * 60 + m;
      if (total >= minMins && total <= maxMins) hourMinutes.push(m);
    }
    if (!hourMinutes.length) return;
    const minute = hourMinutes.includes(selectedMinute) ? selectedMinute : hourMinutes[0];
    onChange?.(fromMinutesOfDay(hour * 60 + minute));
  };

  const pickMinute = (minute) => {
    const hour = Number.isFinite(selectedHour) ? selectedHour : hours[0];
    if (hour == null) return;
    onChange?.(fromMinutesOfDay(hour * 60 + minute));
    setOpen(false);
  };

  const display = value ? formatHhmmTo12h(value) : placeholder;

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          className={`att-tt-time-picker__menu${menuPos.openUp ? ' is-up' : ''}`}
          role="listbox"
          style={{
            position: 'fixed',
            top: menuPos.openUp ? 'auto' : menuPos.top,
            bottom: menuPos.openUp ? window.innerHeight - menuPos.top : 'auto',
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 100050
          }}
        >
          <div className="att-tt-time-picker__col">
            <div className="att-tt-time-picker__col-title">Hour</div>
            <div className="att-tt-time-picker__list">
              {hours.map((h) => (
                <button
                  key={`h-${h}`}
                  type="button"
                  className={`att-tt-time-picker__item${h === selectedHour ? ' is-active' : ''}`}
                  onClick={() => pickHour(h)}
                >
                  {formatHourTo12hLabel(h)}
                </button>
              ))}
            </div>
          </div>
          <div className="att-tt-time-picker__col">
            <div className="att-tt-time-picker__col-title">Min</div>
            <div className="att-tt-time-picker__list">
              {minutes.map((m) => (
                <button
                  key={`m-${m}`}
                  type="button"
                  className={`att-tt-time-picker__item${m === selectedMinute ? ' is-active' : ''}`}
                  onClick={() => pickMinute(m)}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`att-tt-time-picker${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <button
        type="button"
        className={`att-tt-time-picker__control${open ? ' is-open' : ''}${!value ? ' is-placeholder' : ''}`}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        disabled={disabled}
      >
        <span className="att-tt-time-picker__icon" aria-hidden>
          <i className="bi bi-clock" />
        </span>
        <span className="att-tt-time-picker__value">{display}</span>
        <span className="att-tt-time-picker__chevron" aria-hidden>
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
        </span>
      </button>
      {menu}
    </div>
  );
};

/**
 * IN locality only — create a time edit request (no timeLogId).
 * AU must not use this modal.
 */
const TimeEditRequestModal = ({
  show,
  onHide,
  assigneeId,
  timeZone = 'Asia/Kolkata',
  defaultDate = '',
  defaultStartTime = '',
  defaultEndTime = '',
  lockToBreak = false,
  canPickUserId = false
}) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [projectError, setProjectError] = useState('');
  const [taskError, setTaskError] = useState('');
  const [reasonError, setReasonError] = useState('');

  const { data: projectsRes, isFetching: isProjectsLoading } = useGetProjectsQuery(
    { skip: 0, limit: 500 },
    { skip: !show }
  );

  const { data: tasksListRes, isFetching: isTasksLoading } = useGetTasksListQuery(
    {
      projectId: normalizeId(selectedProject?.value),
      assigneeId: normalizeId(assigneeId),
      skip: 0,
      limit: 200
    },
    {
      skip: !show || !normalizeId(selectedProject?.value) || !normalizeId(assigneeId)
    }
  );

  const [createRequest, { isLoading: isSubmitting }] = useCreateTimeEditRequestMutation();

  const projectOptions = useMemo(() => {
    const rows = extractListRows(projectsRes);
    return rows
      .map((p) => {
        const id = normalizeId(p?._id || p?.id);
        if (!id) return null;
        const key = p?.projectKey ? `${p.projectKey} — ` : '';
        return {
          value: id,
          label: `${key}${p?.name || 'Untitled project'}`,
          project: p
        };
      })
      .filter(Boolean)
      .sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
      );
  }, [projectsRes]);

  const taskOptions = useMemo(() => {
    const rows = extractListRows(tasksListRes);
    return rows
      .map((task) => {
        const taskId = normalizeId(task?.taskId || task?._id || task?.id);
        if (!taskId) return null;
        return {
          value: taskId,
          label: `${task?.taskKey ? `${task.taskKey} — ` : ''}${task?.title || 'Untitled'}`,
          task
        };
      })
      .filter(Boolean)
      .sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
      );
  }, [tasksListRes]);

  useEffect(() => {
    if (!show) return;
    setSelectedProject(null);
    setSelectedTask(null);
    setSelectedDate(defaultDate || moment().format('YYYY-MM-DD'));
    setStartTime(defaultStartTime || '');
    setEndTime(defaultEndTime || '');
    setReason('');
    setProjectError('');
    setTaskError('');
    setReasonError('');
  }, [show, defaultDate, defaultStartTime, defaultEndTime]);

  const resetAndClose = () => {
    onHide?.();
  };

  const handleSubmit = async () => {
    setProjectError('');
    setTaskError('');
    setReasonError('');

    if (!selectedProject?.value) {
      setProjectError('Please select a project');
      return;
    }
    if (!selectedTask?.value) {
      setTaskError('Please select a task');
      return;
    }
    if (!selectedDate || !startTime || !endTime) {
      notification('Please select date, start and end time.', TOAST_ERROR);
      return;
    }

    const trimmedReason = String(reason || '').trim();
    if (!trimmedReason) {
      setReasonError('Reason is required');
      return;
    }

    if (lockToBreak && defaultStartTime && defaultEndTime) {
      const startMins = toMinutesOfDay(startTime);
      const endMins = toMinutesOfDay(endTime);
      const lo = toMinutesOfDay(defaultStartTime);
      const hi = toMinutesOfDay(defaultEndTime);
      if (
        startMins == null ||
        endMins == null ||
        lo == null ||
        hi == null ||
        startMins < lo ||
        endMins > hi ||
        startMins > hi ||
        endMins < lo
      ) {
        notification(
          `Time must stay inside the break window (${formatHhmmTo12h(defaultStartTime)} – ${formatHhmmTo12h(defaultEndTime)}).`,
          TOAST_ERROR
        );
        return;
      }
    }

    const anchorTs = Date.parse(`${selectedDate}T12:00:00.000Z`);
    const start = wallTimeOnAnchorDayToUtcMs(startTime, anchorTs, timeZone);
    const end = wallTimeOnAnchorDayToUtcMs(endTime, anchorTs, timeZone);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      notification('Please select valid start and end time.', TOAST_ERROR);
      return;
    }
    if (end <= start) {
      notification('End time must be after start time.', TOAST_ERROR);
      return;
    }

    const projectName = String(
      selectedProject?.project?.name || selectedProject?.label || ''
    ).trim();
    // Prefer task.title; strip "KEY — " prefix if falling back to select label
    const rawTaskLabel = String(selectedTask?.label || '').trim();
    const taskName = String(
      selectedTask?.task?.title ||
        rawTaskLabel.replace(/^[^\u2014—-]+[\u2014—-]\s*/, '') ||
        rawTaskLabel
    ).trim();

    if (!projectName || !taskName) {
      notification('Please select a valid project and task.', TOAST_ERROR);
      return;
    }

    // date = midnight UTC of the selected day (Unix ms)
    const date = moment.utc(selectedDate, 'YYYY-MM-DD').startOf('day').valueOf();

    const body = {
      taskId: String(selectedTask.value),
      taskName,
      projectId: String(selectedProject.value),
      projectName,
      date,
      start,
      end,
      reason: trimmedReason
    };

    // Admin/HR may submit for another user; never send timeLogId
    if (canPickUserId && assigneeId) {
      body.userId = String(assigneeId);
    }

    try {
      const res = await createRequest(body).unwrap();
      notification(res?.statusMessage || 'Request submitted for approval', TOAST_SUCCESS);
      resetAndClose();
    } catch (err) {
      notification(
        err?.data?.statusMessage ||
          err?.data?.message ||
          err?.data?.data?.message ||
          'Failed to submit time edit request.',
        TOAST_ERROR
      );
    }
  };

  return (
    <Modal
      centered
      animation={false}
      show={show}
      onHide={resetAndClose}
      dialogClassName="att-tt-fill-break-modal"
    >
      <Modal.Header closeButton className="att-tt-fill-break-modal__header">
        <div>
          <h5 className="modal-title mb-1">
            {lockToBreak ? 'Request time for break' : 'Request time entry'}
          </h5>
          <p className="att-tt-fill-break-modal__subtitle mb-0">
            Submit for Admin/HR approval — time is added after approval
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="att-tt-fill-break-modal__body">
        {lockToBreak && defaultStartTime && defaultEndTime ? (
          <div className="att-tt-fill-break-modal__window mb-3" role="note">
            <span className="att-tt-fill-break-modal__window-label">Break window</span>
            <span className="att-tt-fill-break-modal__window-value">
              {formatHhmmTo12h(defaultStartTime)} – {formatHhmmTo12h(defaultEndTime)}
            </span>
            {selectedDate ? (
              <span className="att-tt-fill-break-modal__window-date">
                {moment(selectedDate, 'YYYY-MM-DD').format('DD MMM YYYY')}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="att-tt-fill-break-modal__field mb-3">
          <label className="att-tt-fill-break-modal__label">
            Project <span className="text-danger">*</span>
          </label>
          <Select
            classNamePrefix="att-tt-fill-select"
            options={projectOptions}
            value={selectedProject}
            isLoading={isProjectsLoading}
            onChange={(opt) => {
              setSelectedProject(opt);
              setSelectedTask(null);
              setProjectError('');
              setTaskError('');
            }}
            placeholder="Select project..."
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectStyles(projectError)}
          />
          {projectError ? <small className="text-danger d-block mt-1">{projectError}</small> : null}
        </div>

        <div className="att-tt-fill-break-modal__field mb-3">
          <label className="att-tt-fill-break-modal__label">
            Task <span className="text-danger">*</span>
          </label>
          <Select
            classNamePrefix="att-tt-fill-select"
            options={taskOptions}
            value={selectedTask}
            isDisabled={!selectedProject}
            isLoading={isTasksLoading}
            onChange={(opt) => {
              setSelectedTask(opt);
              setTaskError('');
            }}
            placeholder={
              !selectedProject
                ? 'Select project first...'
                : isTasksLoading
                  ? 'Loading tasks...'
                  : 'Select task...'
            }
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectStyles(taskError)}
          />
          {taskError ? <small className="text-danger d-block mt-1">{taskError}</small> : null}
        </div>

        <div className="mb-3">
          <label className="form-label">
            Date <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            disabled={lockToBreak}
          />
        </div>

        <div className="att-tt-fill-break-modal__times mb-3">
          <div className="att-tt-fill-break-modal__field">
            <label className="att-tt-fill-break-modal__label">
              Start time <span className="text-danger">*</span>
            </label>
            <TimePicker12h
              value={startTime}
              minTime={lockToBreak ? defaultStartTime : '00:00'}
              maxTime={lockToBreak ? defaultEndTime : endTime || '23:59'}
              onChange={(next) => {
                setStartTime(next);
                if (endTime && next && next > endTime) setEndTime(next);
              }}
              placeholder="Select start"
            />
          </div>
          <div className="att-tt-fill-break-modal__field">
            <label className="att-tt-fill-break-modal__label">
              End time <span className="text-danger">*</span>
            </label>
            <TimePicker12h
              value={endTime}
              minTime={startTime || (lockToBreak ? defaultStartTime : '00:00')}
              maxTime={lockToBreak ? defaultEndTime : '23:59'}
              onChange={(next) => setEndTime(next)}
              placeholder="Select end"
            />
          </div>
        </div>

        <div className="mb-1">
          <label className="form-label">
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            className={`form-control${reasonError ? ' is-invalid' : ''}`}
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setReasonError('');
            }}
            placeholder="Why should this time be added?"
          />
          {reasonError ? <div className="invalid-feedback d-block">{reasonError}</div> : null}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={resetAndClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? 'Submitting...' : 'Submit request'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default TimeEditRequestModal;
